type TabEvent = Pick<KeyboardEvent, 'type' | 'key' | 'preventDefault'> & { currentTarget: HTMLElement };

// jQuery is only needed for the Bootstrap 4 tab plugin (`shown.bs.tab` is a jQuery-only event)
declare const $: (target: string | HTMLElement) => {
  on: (events: string, handler: (evt: TabEvent) => void) => void;
  tab: (action: 'show') => void;
};

const SELECTORS = {
  focusable: 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  tabPanel: {
    tabpanel: '[role="tabpanel"]',
    tablist: '[role="tablist"]',
    tabs: '.js-accessible-tab',
    tabwrp: '.js-accessible-tabs-wrp'
  },
  modal: {
    closeButton: '.js-close-accessible-dialog'
  }
};

const open = (modal: HTMLElement, firstFocusable?: HTMLElement): void => {
  // need to wait some time since some modals are hidden with display: none
  setTimeout(() => {
    modal.setAttribute('aria-hidden', 'false');
    modal.setAttribute('aria-modal', 'true');
    firstFocusable?.focus();
  }, 200);
};

const close = (modal: HTMLElement, openBtns: ArrayLike<HTMLElement>): void => {
  modal.setAttribute('aria-hidden', 'true');
  modal.setAttribute('aria-modal', 'false');
  Array.from(openBtns).forEach(btn => btn.focus());
};

/**
 * Adds the accessible open/close/focus-trap behaviour to a modal. Visibility toggling has to be handled separately.
 * Follows the ARIA Dialog pattern @see https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/examples/dialog/
 * @param openBtns the button(s) that trigger the modal, the modal is taken from the first one's `data-target`
 */
const accessibleModal = (openBtns: ArrayLike<HTMLElement>): void => {
  const target = openBtns[0]?.dataset.target;
  const modal = target ? document.querySelector<HTMLElement>(target) : null;
  if (!modal) return;

  const focusableElements = modal.querySelectorAll<HTMLElement>(SELECTORS.focusable);
  const firstFocusable = focusableElements[0];
  const lastFocusable = focusableElements[focusableElements.length - 1];

  const toggle = (evt: Event): void => {
    if (evt.type === 'keydown' && !['Enter', ' '].includes((evt as KeyboardEvent).key)) return;

    if (openBtns[0].getAttribute('aria-expanded') === 'true') close(modal, openBtns);
    else open(modal, firstFocusable);
  };

  Array.from(openBtns).forEach(btn => {
    btn.addEventListener('click', toggle);
    btn.addEventListener('keydown', toggle);
  });

  modal.querySelectorAll(SELECTORS.modal.closeButton).forEach(btn => {
    btn.addEventListener('click', () => close(modal, openBtns));
  });

  modal.addEventListener('keydown', e => {
    if (e.key === 'Escape') close(modal, openBtns);
    if (e.key !== 'Tab') return;

    if (e.shiftKey && document.activeElement === firstFocusable) {
      e.preventDefault();
      lastFocusable.focus();
    } else if (!e.shiftKey && document.activeElement === lastFocusable) {
      e.preventDefault();
      firstFocusable.focus();
    }
  });
};

const select = (tab: HTMLElement, evt: TabEvent): void => {
  const wrp = tab.closest(SELECTORS.tabPanel.tabwrp);
  const panel = document.getElementById(tab.getAttribute('aria-controls') ?? '');

  document.querySelectorAll(SELECTORS.tabPanel.tabs).forEach(el => {
    el.setAttribute('aria-selected', 'false');
    el.setAttribute('tabindex', '-1');
  });
  tab.setAttribute('aria-selected', 'true');
  tab.setAttribute('tabindex', '0');
  if (evt.type !== 'click') $(tab).tab('show');

  wrp?.querySelectorAll(SELECTORS.tabPanel.tabs).forEach(el => el.classList.remove('active'));
  tab.classList.add('active');
  wrp?.querySelectorAll(SELECTORS.tabPanel.tabpanel).forEach(el => {
    el.classList.remove('active');
    el.setAttribute('aria-hidden', 'true');
  });
  panel?.classList.add('active');
  panel?.setAttribute('aria-hidden', 'false');
};

const navigate = (tab: HTMLElement, tablist: Element, evt: TabEvent): void => {
  const orientation = tablist.getAttribute('aria-orientation');
  const horizontal = (evt.key === 'ArrowRight' || evt.key === 'ArrowLeft') && (!orientation || orientation === 'horizontal');
  const vertical = (evt.key === 'ArrowDown' || evt.key === 'ArrowUp') && orientation === 'vertical';
  if (!horizontal && !vertical) return;

  const tabs = Array.from(tablist.querySelectorAll<HTMLElement>(SELECTORS.tabPanel.tabs));
  const index = tabs.indexOf(tab);
  const first = tabs[0];
  const last = tabs[tabs.length - 1];

  evt.preventDefault();
  if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') (tab === last ? first : tabs[index + 1]).focus();
  else (tab === first ? last : tabs[index - 1]).focus();
};

/**
 * Adds the accessible keyboard navigation to tablists. Panel visibility has to be handled separately.
 * Follows the ARIA Tabs pattern @see https://www.w3.org/WAI/ARIA/apg/patterns/tabpanel/
 */
const accessibleTablist = (): void => {
  $(SELECTORS.tabPanel.tabs).on('keydown click shown.bs.tab', evt => {
    const tab = evt.currentTarget;
    const tablist = tab.closest(SELECTORS.tabPanel.tablist);
    if (!tablist) return;

    if (evt.key === 'Enter' || evt.key === ' ' || evt.type === 'shown' || evt.type === 'click') select(tab, evt);
    else navigate(tab, tablist, evt);
  });
};

/**
 * Focuses the first focusable element inside a section, e.g. when the section is opened.
 */
const focusOnSection = (section: ArrayLike<HTMLElement>): void => {
  section[0]?.querySelector<HTMLElement>(SELECTORS.focusable)?.focus();
};

export { accessibleModal, accessibleTablist, focusOnSection };
